// ─── What: signal pins and trader span lines ──────────────────────────────────
//
// Market signals get a stem rising from the probability line and a shaped head.
// Trader signals get a bare dot on the line — and only for the trader currently
// hovered elsewhere in the UI, which is why they are not drawn by default.
//
// Every pin carries an invisible hit circle behind the visible head, so the hover
// target is the same size whatever the shape.

import { alpha } from '@mui/material/styles';
import type { SignalOverlayData } from '../../../../api/types';
import { MARKET_SIGNAL_TYPES } from '../../../../api/types';
import { clampToDomain } from '../../chartYDomain';
import { nearestOhlcv } from '../../chartData';
import {
    placeTooltip,
    buildMarkerTooltipHtml,
    buildSignalTypeColors,
} from '../../chartTooltips';
import { SIGNAL_TYPE_LABELS, type PreparedSignal } from '../../chartTypes';
import { SIGNAL_TYPE_SHORT_MARKET, FULL_LINE, FULL_MARKER } from '../constants';
import { prepareSignals } from './prepareSignals';
import { pinHeadPath } from './signalShapes';
import type { ChartChrome, DrawCtx } from '../drawContext';

/** The subset of SignalInteractionContext this overlay uses. */
export interface SignalInteraction {
    state?: {
        visibleIds?: Set<string> | null;
        hoveredId?: string | null;
        selectedId?: string | null;
    };
    actions: {
        hover: (id: string | null) => void;
        select: (id: string | null) => void;
    };
}

export interface SignalOverlayDeps {
    signalOverlay: SignalOverlayData;
    hoveredTraderAddress?: string | null;
    signalInteraction: SignalInteraction | null;
    /** Visible span in days; labels are only legible below a month. */
    spanDays: number;
    chrome: ChartChrome;
}

export function drawSignalOverlay(ctx: DrawCtx, deps: SignalOverlayDeps): void {
    const {
        svg,
        layout,
        theme,
        colors,
        xScale,
        xDomain,
        yScale,
        probDomain,
        series,
        questionMap,
        tooltipRef,
    } = ctx;
    const { signalOverlay, hoveredTraderAddress, signalInteraction, chrome } =
        deps;
    const { tc } = colors;

    const typeColors = buildSignalTypeColors(theme.palette);

    const getProbY = (conditionId: string, date: Date): number | null => {
        const s = series.find(s => s.conditionId === conditionId);
        if (!s || s.data.length === 0) return null;
        const pt = nearestOhlcv(s.data, date);
        return pt ? yScale(clampToDomain(pt.close, probDomain.domain)) : null;
    };

    const prepared = prepareSignals({
        byMarket: signalOverlay.byMarket,
        colorMap: signalOverlay.colorMap,
        typeColors,
        fallbackColor: theme.palette.primary.main,
        xDomain,
        x: d => xScale(d),
        getProbY,
    });

    const marketSignals = prepared.filter(s =>
        MARKET_SIGNAL_TYPES.has(s.signalType)
    );
    const hoveredTraderSignals = hoveredTraderAddress
        ? prepared.filter(
              s =>
                  !MARKET_SIGNAL_TYPES.has(s.signalType) &&
                  s.address === hoveredTraderAddress
          )
        : [];
    const renderSignals = [...marketSignals, ...hoveredTraderSignals];

    const signalGroup = svg
        .append('g')
        .attr('clip-path', `url(#${layout.clipIds.sig})`)
        .attr('class', 'signal-overlay');

    // ── Per-trader span lines ─────────────────────────────────────────────
    // Solid from first to last known activity, dashed onward while still open.
    if (hoveredTraderSignals.length > 0) {
        const byAddress = new Map<string, PreparedSignal[]>();
        for (const s of hoveredTraderSignals) {
            const addr = s.address!;
            if (!byAddress.has(addr)) byAddress.set(addr, []);
            byAddress.get(addr)!.push(s);
        }

        for (const [address, sigs] of byAddress) {
            sigs.sort((a, b) => a.openedAt.getTime() - b.openedAt.getTime());
            const first = sigs[0];
            const last = sigs[sigs.length - 1];
            const isStillOpen = last.status === 'open' && !last.closedAt;
            const solidEnd = last.closedAt ?? last.lastSeenAt ?? last.openedAt;
            const clampedEnd = solidEnd > xDomain[1] ? xDomain[1] : solidEnd;
            const pxEnd = xScale(clampedEnd);
            const pyEnd = getProbY(first.conditionId, clampedEnd) ?? first.py;

            if (pxEnd > first.px + 2) {
                signalGroup
                    .append('line')
                    .attr('class', 'trader-span trader-span-solid')
                    .attr('data-trader-addr', address)
                    .attr('x1', first.px)
                    .attr('y1', first.py)
                    .attr('x2', pxEnd)
                    .attr('y2', pyEnd)
                    .attr('stroke', first.color)
                    .attr('stroke-width', 1)
                    .attr('opacity', FULL_LINE)
                    .style('pointer-events', 'none');
            }

            if (isStillOpen) {
                const tailStart =
                    last.closedAt ?? last.lastSeenAt ?? last.openedAt;
                const tailPxStart = Math.min(
                    xScale(tailStart),
                    xScale(xDomain[1])
                );
                const tailPxEnd = xScale(xDomain[1]);
                const tailPyStart =
                    getProbY(first.conditionId, tailStart) ?? first.py;
                const tailPyEnd =
                    getProbY(first.conditionId, xDomain[1]) ?? tailPyStart;
                if (tailPxEnd > tailPxStart + 4) {
                    signalGroup
                        .append('line')
                        .attr('class', 'trader-span trader-span-open-tail')
                        .attr('data-trader-addr', address)
                        .attr('x1', tailPxStart)
                        .attr('y1', tailPyStart)
                        .attr('x2', tailPxEnd)
                        .attr('y2', tailPyEnd)
                        .attr('stroke', first.color)
                        .attr('stroke-width', 1)
                        .attr('stroke-dasharray', '2 4')
                        .attr('opacity', FULL_LINE * 0.5)
                        .style('pointer-events', 'none');
                }
            }
        }
    }

    // ── Pins ──────────────────────────────────────────────────────────────
    const showLabels = deps.spanDays <= 30;

    for (const sig of renderSignals) {
        const isMarketSig = MARKET_SIGNAL_TYPES.has(sig.signalType);
        const isClosed = sig.status === 'closed';
        const attachY = sig.py;

        const sigState = signalInteraction?.state;
        const isVisible =
            !sigState?.visibleIds || sigState.visibleIds.has(sig.id);
        const isHovered = sigState?.hoveredId === sig.id;
        const isSelected = sigState?.selectedId === sig.id;

        if (isMarketSig) {
            const stemLen = 20 + sig.score * 12;
            // Volume anomalies get a smaller head: they are the most numerous,
            // and at full size they crowd everything else off the line.
            let headR = 5;
            if (sig.signalType === 'volume_anomaly') headR = headR / 2;
            const hitR = 10;
            const tipY = attachY - stemLen;

            const headColor =
                sig.direction === 'bullish'
                    ? '#10b981'
                    : sig.direction === 'bearish'
                      ? '#ef4444'
                      : sig.color;

            const baseOpacity = isVisible ? (isClosed ? 0.45 : FULL_MARKER) : 0.08;
            const renderR = isSelected
                ? headR * 1.5
                : isHovered
                  ? headR * 1.35
                  : headR;
            const renderOpacity = isSelected || isHovered ? 1 : baseOpacity;
            const strokeW = isSelected ? 2.5 : 1.5;

            const visStem = signalGroup
                .append('line')
                .datum(sig)
                .attr('class', 'sig-marker')
                .attr('data-signal-id', sig.id)
                .attr('x1', sig.px)
                .attr('y1', attachY)
                .attr('x2', sig.px)
                .attr('y2', tipY + renderR)
                .attr('stroke', headColor)
                .attr('stroke-width', isSelected ? 1.5 : 1)
                .attr('opacity', renderOpacity * 0.5)
                .style('pointer-events', 'none');

            const visHead = signalGroup
                .append('path')
                .datum(sig)
                .attr('class', 'sig-marker')
                .attr('data-signal-id', sig.id)
                .attr(
                    'd',
                    pinHeadPath(
                        sig.px,
                        tipY,
                        renderR,
                        sig.signalType,
                        sig.direction
                    )
                )
                .attr('fill', isClosed ? 'none' : headColor)
                .attr('stroke', isSelected ? '#fff' : headColor)
                .attr('stroke-width', strokeW)
                .attr('opacity', renderOpacity)
                .style('pointer-events', 'none');

            if (isSelected) {
                signalGroup
                    .append('circle')
                    .attr('class', 'sig-marker sig-selection-ring')
                    .attr('data-signal-id', sig.id)
                    .attr('cx', sig.px)
                    .attr('cy', tipY)
                    .attr('r', renderR + 4)
                    .attr('fill', 'none')
                    .attr('stroke', headColor)
                    .attr('stroke-width', 1.5)
                    .attr('stroke-dasharray', '3 2')
                    .attr('opacity', 0.7)
                    .style('pointer-events', 'none');
            }

            signalGroup
                .append('circle')
                .datum(sig)
                .attr('class', 'sig-marker sig-hit')
                .attr('data-signal-id', sig.id)
                .attr('cx', sig.px)
                .attr('cy', tipY)
                .attr('r', hitR)
                .attr('fill', 'transparent')
                .attr('stroke', 'none')
                .style('cursor', isVisible ? 'pointer' : 'default')
                .on('mouseenter', (event: MouseEvent) => {
                    if (!isVisible) return;
                    chrome.suppressForMarker();
                    visHead
                        .attr(
                            'd',
                            pinHeadPath(
                                sig.px,
                                tipY,
                                headR * 1.5,
                                sig.signalType,
                                sig.direction
                            )
                        )
                        .attr('stroke-width', 2)
                        .attr('opacity', 1);
                    visStem.attr('stroke-width', 1.5).attr('opacity', baseOpacity);
                    signalInteraction?.actions.hover(sig.id);
                    placeTooltip(
                        tooltipRef.current,
                        event,
                        buildMarkerTooltipHtml(
                            sig,
                            questionMap as Map<string, string>,
                            tc
                        )
                    );
                })
                .on('mousemove', (event: MouseEvent) => {
                    placeTooltip(
                        tooltipRef.current,
                        event,
                        tooltipRef.current?.innerHTML ?? ''
                    );
                })
                .on('mouseleave', () => {
                    if (!isVisible) return;
                    const restoreR = isSelected ? headR * 1.5 : headR;
                    const restoreOpacity = isSelected ? 1 : baseOpacity;
                    visHead
                        .attr(
                            'd',
                            pinHeadPath(
                                sig.px,
                                tipY,
                                restoreR,
                                sig.signalType,
                                sig.direction
                            )
                        )
                        .attr('stroke-width', isSelected ? 2.5 : 1.5)
                        .attr('opacity', restoreOpacity);
                    visStem
                        .attr('stroke-width', isSelected ? 1.5 : 1)
                        .attr('opacity', restoreOpacity * 0.5);
                    signalInteraction?.actions.hover(null);
                    chrome.hideTooltip();
                })
                .on('click', () => {
                    if (!isVisible) return;
                    signalInteraction?.actions.select(
                        isSelected ? null : sig.id
                    );
                });

            if (showLabels) {
                signalGroup
                    .append('text')
                    .datum(sig)
                    .attr('class', 'sig-label')
                    .attr('x', sig.px)
                    .attr('y', tipY - headR - 3)
                    .attr('text-anchor', 'middle')
                    .attr('font-size', '7px')
                    .attr('font-family', 'monospace')
                    .attr('font-weight', 700)
                    .attr('fill', headColor)
                    .attr('opacity', isVisible ? 0.75 : 0.08)
                    .style('pointer-events', 'none')
                    .text(
                        SIGNAL_TYPE_SHORT_MARKET[sig.signalType] ??
                            sig.signalType
                    );
            }
        } else {
            // Trader signal dot — same interaction, simpler shape, no stem.
            const dotR = 3;
            const hitR = 8;
            const baseOpacity = isVisible ? (isClosed ? 0.5 : FULL_MARKER) : 0.08;
            const renderDotR = isSelected
                ? dotR * 1.5
                : isHovered
                  ? dotR * 1.35
                  : dotR;

            const visDot = signalGroup
                .append('circle')
                .datum(sig)
                .attr('class', 'sig-marker')
                .attr('data-signal-id', sig.id)
                .attr('cx', sig.px)
                .attr('cy', attachY)
                .attr('r', renderDotR)
                .attr('fill', isClosed ? 'none' : alpha(sig.color, 0.75))
                .attr('stroke', isSelected ? '#fff' : sig.color)
                .attr('stroke-width', isSelected ? 2.5 : 1.5)
                .attr('opacity', isSelected || isHovered ? 1 : baseOpacity)
                .style('pointer-events', 'none');

            signalGroup
                .append('circle')
                .datum(sig)
                .attr('class', 'sig-marker sig-hit')
                .attr('data-signal-id', sig.id)
                .attr('cx', sig.px)
                .attr('cy', attachY)
                .attr('r', hitR)
                .attr('fill', 'transparent')
                .attr('stroke', 'none')
                .style('cursor', isVisible ? 'pointer' : 'default')
                .on('mouseenter', (event: MouseEvent) => {
                    if (!isVisible) return;
                    chrome.suppressForMarker();
                    visDot
                        .attr('r', dotR * 1.5)
                        .attr('stroke-width', 2)
                        .attr('opacity', 1);
                    signalInteraction?.actions.hover(sig.id);
                    placeTooltip(
                        tooltipRef.current,
                        event,
                        buildMarkerTooltipHtml(
                            sig,
                            questionMap as Map<string, string>,
                            tc
                        )
                    );
                })
                .on('mousemove', (event: MouseEvent) => {
                    placeTooltip(
                        tooltipRef.current,
                        event,
                        tooltipRef.current?.innerHTML ?? ''
                    );
                })
                .on('mouseleave', () => {
                    if (!isVisible) return;
                    visDot
                        .attr('r', isSelected ? dotR * 1.5 : dotR)
                        .attr('stroke-width', isSelected ? 2.5 : 1.5)
                        .attr('opacity', isSelected ? 1 : baseOpacity);
                    signalInteraction?.actions.hover(null);
                    chrome.hideTooltip();
                })
                .on('click', () => {
                    if (!isVisible) return;
                    signalInteraction?.actions.select(
                        isSelected ? null : sig.id
                    );
                });

            if (showLabels) {
                signalGroup
                    .append('text')
                    .datum(sig)
                    .attr('class', 'sig-label')
                    .attr('x', sig.px)
                    .attr('y', attachY - dotR - 3)
                    .attr('text-anchor', 'middle')
                    .attr('font-size', '7px')
                    .attr('font-family', 'monospace')
                    .attr('font-weight', 400)
                    .attr('fill', sig.color)
                    .attr('opacity', isVisible ? 0.7 : 0.08)
                    .style('pointer-events', 'none')
                    .text(SIGNAL_TYPE_LABELS[sig.signalType] ?? sig.signalType);
            }
        }
    }
}
